import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { LogOut, AlertTriangle, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface LogoutConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  username?: string;
}

const LogoutConfirmationModal: React.FC<LogoutConfirmationModalProps> = ({
  show,
  onHide,
  onConfirm,
  username
}) => {
  const { themeConfig } = useTheme();

  return (
    <>
      <style>{`
        .logout-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          background: ${themeConfig.colors.surface};
          color: ${themeConfig.colors.text};
        }
        
        .logout-modal .modal-header {
          border: none;
          padding: 24px 24px 0 24px;
        }
        
        .logout-modal .modal-body {
          padding: 16px 24px;
        }
        
        .logout-modal .modal-footer {
          border: none;
          padding: 0 24px 24px 24px;
        }
        
        .logout-modal .btn-modern {
          border-radius: 12px;
          padding: 12px 24px;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
        }
        
        .logout-modal .btn-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }
        
        .logout-modal .btn-danger-modern {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        
        .logout-modal .btn-secondary-modern {
          background: ${themeConfig.colors.surface};
          color: ${themeConfig.colors.text};
          border: 1px solid ${themeConfig.colors.border};
        }
        
        .logout-modal .icon-wrapper {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          border-radius: 50%;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }
        
        .logout-modal .close-btn {
          background: none;
          border: none;
          color: ${themeConfig.colors.textSecondary};
          padding: 8px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .logout-modal .close-btn:hover {
          background: ${themeConfig.colors.border};
          color: ${themeConfig.colors.text};
        }
      `}</style>

      <Modal
        show={show}
        onHide={onHide}
        centered
        className="logout-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="d-flex justify-content-between align-items-start">
          <div></div>
          <button className="close-btn" onClick={onHide}>
            <X size={20} />
          </button>
        </Modal.Header>
        
        <Modal.Body className="text-center">
          <div className="icon-wrapper">
            <LogOut size={28} style={{ color: '#ef4444' }} />
          </div>
          
          <h4 className="fw-bold mb-2">Confirm Logout</h4>
          <p className="text-muted mb-0">
            Are you sure you want to logout{username ? `, ${username}` : ''}? 
            <br />
            <small>You'll need to sign in again to access your account.</small>
          </p>
        </Modal.Body>
        
        <Modal.Footer className="d-flex gap-3 justify-content-center">
          <Button
            className="btn-modern btn-secondary-modern"
            onClick={onHide}
            style={{ minWidth: '120px' }}
          >
            Cancel
          </Button>
          <Button
            className="btn-modern btn-danger-modern"
            onClick={onConfirm}
            style={{ minWidth: '120px' }}
          >
            <LogOut size={16} className="me-2" />
            Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LogoutConfirmationModal;